import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, FileText, Briefcase, Euro, Users } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  STATI_GARA, STATI_COMMESSA, deadlineStyle, deadlineLabel, fmtData, fmtDataOra, fmtEuro, nomeDipendente,
} from "../lib/helpers";

function Kpi({ icon: Icon, label, value, sub, tone = "default", testid }) {
  const tones = {
    default: "border-slate-200",
    danger: "border-red-300 bg-red-50",
    warn: "border-amber-300 bg-amber-50",
  };
  return (
    <div data-testid={testid} className={`bg-white border rounded-md p-4 ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="mt-2 font-display text-3xl font-bold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <div data-testid="dashboard-loading" className="py-20 text-center text-slate-500 text-sm">Caricamento dashboard…</div>;
  }

  const { stats, gare_scadenza, commesse_scadenza, commesse_ritardo, carico } = data;
  const gare7 = gare_scadenza.filter((g) => g.giorni_rimanenti <= 7);
  const maxCarico = Math.max(1, ...carico.map((c) => c.totale));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Riepilogo di gare, commesse e carico del team.</p>
      </div>

      {commesse_ritardo.length > 0 && (
        <div data-testid="alert-ritardi-banner" className="flex items-start gap-3 bg-red-50 border border-red-300 rounded-md px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-red-700 shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-semibold text-red-800">{commesse_ritardo.length} commesse in ritardo sulla consegna.</span>
            <span className="text-red-700"> {commesse_ritardo.map((c) => c.codice).join(", ")} — </span>
            <Link to="/commesse" className="font-semibold underline text-red-800">vai alle commesse</Link>
          </div>
        </div>
      )}

      <section className="bg-white border border-slate-200 rounded-md" data-testid="dashboard-gare-7gg">
        <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <AlertTriangle className={`h-4 w-4 ${gare7.length ? "text-red-600" : "text-slate-400"}`} />
            Gare in scadenza entro 7 giorni
          </h2>
          <Badge variant="outline" className={`font-code ${gare7.length ? "bg-red-100 text-red-800 border-red-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{gare7.length}</Badge>
        </header>
        {gare7.length === 0 ? (
          <div className="px-4 py-4 text-sm text-slate-500">Nessuna gara in scadenza nei prossimi 7 giorni.</div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {gare7.map((g) => {
                const st = deadlineStyle(g.giorni_rimanenti);
                return (
                  <tr key={g.id} data-testid={`dash-gara7-row-${g.id}`} className={`border-b border-slate-100 last:border-0 ${st.row}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-slate-800">{g.denominazione}</div>
                      <div className="text-xs text-slate-500">{g.ente_banditore}</div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-600 whitespace-nowrap">{fmtDataOra(g.scadenza_offerta)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Badge variant="outline" className={`${st.badge} font-code`}>{deadlineLabel(g.giorni_rimanenti)}</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi testid="kpi-gare-attive" icon={FileText} label="Gare attive" value={stats.gare_attive}
          sub={`${stats.gare_scadenza_7} in scadenza entro 7 giorni`} tone={stats.gare_scadenza_7 > 0 ? "danger" : "default"} />
        <Kpi testid="kpi-commesse-corso" icon={Briefcase} label="Commesse in corso" value={stats.commesse_in_corso}
          sub={`${stats.commesse_in_ritardo} in ritardo`} tone={stats.commesse_in_ritardo > 0 ? "warn" : "default"} />
        {user?.role === "admin" && (
          <Kpi testid="kpi-valore-portafoglio" icon={Euro} label="Valore portafoglio"
            value={fmtEuro(stats.valore_commesse || 0)} sub={`Da incassare: ${fmtEuro(stats.da_incassare || 0)}`} />
        )}
        <Kpi testid="kpi-esiti-gare" icon={Users} label="Esito gare" value={`${stats.gare_vinte} vinte`}
          sub={`${stats.gare_perse} perse/escluse`} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <section className="bg-white border border-slate-200 rounded-md" data-testid="dashboard-gare-scadenza">
            <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-800">Gare in scadenza (30 giorni)</h2>
              <Link to="/gare" className="text-xs font-semibold text-blue-900 hover:underline">Vedi tutte</Link>
            </header>
            {gare_scadenza.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-500">Nessuna gara in scadenza nei prossimi 30 giorni.</div>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {gare_scadenza.map((g) => {
                    const st = deadlineStyle(g.giorni_rimanenti);
                    return (
                      <tr key={g.id} data-testid={`dash-gara-row-${g.id}`} className={`border-b border-slate-100 last:border-0 ${st.row}`}>
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-slate-800">{g.denominazione}</div>
                          <div className="text-xs text-slate-500">{g.ente_banditore}</div>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-slate-600 whitespace-nowrap">{fmtDataOra(g.scadenza_offerta)}</td>
                        <td className="px-4 py-2.5 text-right">
                          <Badge variant="outline" className={`${st.badge} font-code`}>{deadlineLabel(g.giorni_rimanenti)}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>

          <section className="bg-white border border-slate-200 rounded-md" data-testid="dashboard-commesse-scadenza">
            <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-800">Commesse in scadenza / in ritardo</h2>
              <Link to="/commesse" className="text-xs font-semibold text-blue-900 hover:underline">Vedi tutte</Link>
            </header>
            {commesse_scadenza.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-500">Nessuna commessa in scadenza nei prossimi 30 giorni.</div>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {commesse_scadenza.map((c) => {
                    const st = deadlineStyle(c.giorni_rimanenti);
                    return (
                      <tr key={c.id} data-testid={`dash-commessa-row-${c.id}`} className={`border-b border-slate-100 last:border-0 ${st.row}`}>
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-slate-800">
                            <span className="font-code text-xs text-slate-500 mr-2">{c.codice}</span>{c.denominazione}
                          </div>
                          <div className="text-xs text-slate-500">{c.committente} · {STATI_COMMESSA[c.stato]}</div>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-slate-600 whitespace-nowrap">{fmtData(c.scadenza)}</td>
                        <td className="px-4 py-2.5 text-right">
                          <Badge variant="outline" className={`${st.badge} font-code`}>{deadlineLabel(c.giorni_rimanenti)}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </div>

        <section className="bg-white border border-slate-200 rounded-md self-start" data-testid="dashboard-carico">
          <header className="px-4 py-3 border-b border-slate-200">
            <h2 className="text-base font-semibold text-slate-800">Carico di lavoro per operatore</h2>
          </header>
          <div className="p-4 space-y-4">
            {carico.length === 0 && <div className="text-sm text-slate-500">Nessun dipendente attivo.</div>}
            {carico.map(({ dipendente, n_commesse, n_gare, totale }) => {
              const pct = Math.min(100, Math.round((totale / 8) * 100));
              const sovraccarico = totale >= 7;
              return (
                <div key={dipendente.id} data-testid={`carico-${dipendente.id}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-800">{nomeDipendente(dipendente)}</span>
                    <span className={`text-xs font-code ${sovraccarico ? "text-red-700 font-bold" : "text-slate-500"}`}>
                      {n_commesse} comm. · {n_gare} gare
                    </span>
                  </div>
                  <Progress value={pct} className={`h-2 mt-1.5 ${sovraccarico ? "[&>div]:bg-red-600" : "[&>div]:bg-blue-900"}`} />
                  <div className="text-[11px] text-slate-400 mt-0.5">{dipendente.ruolo}</div>
                </div>
              );
            })}
            <p className="text-[11px] text-slate-400 pt-2 border-t border-slate-100">
              Scala indicativa: 8 attività simultanee = 100% carico. In rosso chi supera la soglia.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
